import { computed, inject } from '@angular/core';

import { updateState, withDevtools, withDevToolsStub, withGlitchTracking } from '@angular-architects/ngrx-toolkit';
import { signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

import { ConfigService } from '../services/config/config.service';
import { FarmMembersService } from '../services/farm/farm-members.service';
import { FarmMember } from '../entities/farm/farm-member.entities';
import {
  FarmMemberQueryParams,
  FarmMemberFilters,
  FarmMemberInvitationBase,
  ExistingFarmMemberInvitation,
  FarmMemberUpdateRequest
} from '../entities/farm/farm-member-api.types';
import { PaginatedResponse, EntityCreatedResponse } from '../models/api/api-response.model';
import { StoreErrorHandler } from '../models/errors/app-error';
import { LoadingMap } from './generic-entity.store';
import { withCondition } from './utils/with-condition';
import { upsertMany } from '../helpers/data.helpers';

interface FarmMembersState {
  members: FarmMember[];
  invitations: ExistingFarmMemberInvitation[];
  selectedMember: FarmMember | null;
  currentFarmId: string | null;
  loading: boolean;
  loadingMap: LoadingMap;
  error: string | null;
  totalMembers: number;
  totalInvitations: number;
  currentPage: number;
  pageSize: number;
  hasMoreMembers: boolean;
  searchQuery: string;
  filters: FarmMemberFilters;
}

const initialState: FarmMembersState = {
  members: [],
  invitations: [],
  selectedMember: null,
  currentFarmId: null,
  loading: false,
  loadingMap: {
    loading: false,
    loaded: false,
    error: null,
    pagination: {
      skip: 0,
      limit: 100,
      total: 0,
      loadedCount: 0,
      currentPage: 0,
      totalPages: 0,
      hasMore: false,
      loadedPages: new Set<number>()
    },
    statistics: {
      loading: false,
      loaded: false,
      error: null
    },
    search: {
      loading: false,
      error: null
    }
  },
  error: null,
  totalMembers: 0,
  totalInvitations: 0,
  currentPage: 0,
  pageSize: 100,
  hasMoreMembers: false,
  searchQuery: '',
  filters: {},
};

function updateMemberList(members: FarmMember[], newMember: FarmMember, replaceExisting = true): FarmMember[] {
  const existingIndex = members.findIndex(m => m.user_id === newMember.user_id);
  if (existingIndex >= 0 && replaceExisting) {
    const newMembers = [...members];
    newMembers[existingIndex] = newMember;
    return newMembers;
  } else if (existingIndex === -1) {
    return [...members, newMember];
  }
  return members;
}

export const FarmMembersStore = signalStore(
  withState(initialState),
  withProps(() => ({
    configService: inject(ConfigService),
    farmMembersService: inject(FarmMembersService),
    toastr: inject(ToastrService),
    translate: inject(TranslateService),
  })),
  withComputed(({ members, selectedMember, loading, loadingMap, error, totalMembers, hasMoreMembers, currentFarmId }) => ({
    hasMembers: computed(() => members().length > 0),
    membersCount: computed(() => members().length),
    totalMembersCount: computed(() => totalMembers()),
    isLoading: computed(() => loading()),
    isSearching: computed(() => loadingMap().search.loading),
    hasError: computed(() => error() !== null),
    selectedMemberName: computed(() => selectedMember()?.user?.display_name || null),
    hasCurrentFarmId: computed(() => currentFarmId() !== null),
    hasMoreMembersAvailable: computed(() => hasMoreMembers()),
  })),
  withCondition(({ configService }) => !configService?.environment?.production,
    withDevtools('farm-members', withGlitchTracking()),
    withDevToolsStub('farm-members'),
  ),
  withMethods((store) => ({

    setCurrentFarmId(farmId: string | null): void {
      updateState(store, '[Farm Members] Set Current Farm ID', { currentFarmId: farmId });
    },

    loadFarmMembers(farmId: string, params?: FarmMemberQueryParams, filters?: FarmMemberFilters, append = false): void {
      updateState(store, '[Farm Members] Load Members Start', {
        loading: true,
        error: null,
        currentFarmId: farmId,
        loadingMap: {
          ...store.loadingMap(),
          loading: true,
          error: null
        }
      });

      store.farmMembersService.getFarmMembers(farmId, params, filters).subscribe({
        next: (response: PaginatedResponse<FarmMember>) => {
          const newMembers = append ? upsertMany(store.members(), response.data || [], 'user_id') : (response.data || []);
          updateState(store, '[Farm Members] Load Members Success', {
            members: newMembers,
            totalMembers: response.total,
            currentPage: params?.skip ? Math.floor((params.skip + (response.data?.length || 0)) / (params?.limit || 100)) : 0,
            pageSize: params?.limit || 100,
            hasMoreMembers: (params?.skip || 0) + (response.data?.length || 0) < response.total,
            loading: false,
            error: null,
            filters: filters || {},
            searchQuery: '',
            loadingMap: {
              ...store.loadingMap(),
              loading: false,
              loaded: true,
              error: null
            }
          });

          if (store.selectedMember() === null && newMembers.length > 0) {
            this.selectMember(newMembers[0]);
          }
        },
        error: (error: StoreErrorHandler) => {
          updateState(store, '[Farm Members] Load Members Error', {
            loading: false,
            error: error.message || 'errors.loadFailed',
            loadingMap: {
              ...store.loadingMap(),
              loading: false,
              error: error.message || 'errors.loadFailed'
            }
          });
          store.toastr.error(store.translate.instant(error.message || 'errors.loadFailed'), store.translate.instant('notifications.error'));
        }
      });
    },

    searchFarmMembers(farmId: string, query: string, params?: Omit<FarmMemberQueryParams, 'q'>): void {
      const currentLoadingMap = store.loadingMap();
      updateState(store, '[Farm Members] Search Members Start', {
        loadingMap: {
          ...currentLoadingMap,
          search: { loading: true, error: null }
        },
        searchQuery: query,
        currentFarmId: farmId
      });

      store.farmMembersService.searchFarmMembers(farmId, query, params).subscribe({
        next: (response: PaginatedResponse<FarmMember>) => {
          const mergedMembers = upsertMany(store.members(), response.data, 'user_id');

          updateState(store, '[Farm Members] Search Members Success', {
            members: mergedMembers,
            loadingMap: {
              ...store.loadingMap(),
              search: { loading: false, error: null }
            },
            searchQuery: query
          });
        },
        error: (error: StoreErrorHandler) => {
          const updatedLoadingMap = store.loadingMap();
          updateState(store, '[Farm Members] Search Members Error', {
            loadingMap: {
              ...updatedLoadingMap,
              search: { loading: false, error: error.message || 'errors.loadFailed' }
            }
          });
          store.toastr.error(store.translate.instant(error.message || 'errors.loadFailed'), store.translate.instant('notifications.error'));
        }
      });
    },

    getFarmMember(farmId: string, memberId: string): void {
      updateState(store, '[Farm Members] Load Member Start', { loading: true, error: null });

      store.farmMembersService.getFarmMember(farmId, memberId).subscribe({
        next: (member: FarmMember) => {
          const newMembers = updateMemberList(store.members(), member);

          updateState(store, '[Farm Members] Load Member Success', {
            members: newMembers,
            loading: false,
            error: null
          });
        },
        error: (error: StoreErrorHandler) => {
          updateState(store, '[Farm Members] Load Member Error', {
            loading: false,
            error: error.message || 'errors.loadFailed'
          });
          store.toastr.error(store.translate.instant(error.message || 'errors.loadFailed'), store.translate.instant('notifications.error'));
        }
      });
    },

    inviteFarmMember(farmId: string, invitationData: FarmMemberInvitationBase): void {
      updateState(store, '[Farm Members] Add Member Start', { loading: true, error: null });

      store.farmMembersService.addFarmMember(farmId, invitationData).subscribe({
        next: (response: EntityCreatedResponse) => {
          const newInvitation: ExistingFarmMemberInvitation = {
            ...invitationData,
            id: response.id,
            farm_id: farmId,
          };

          updateState(store, '[Farm Members] Add Member Success', {
            invitations: [...store.invitations(), newInvitation],
            totalInvitations: store.totalInvitations() + 1,
            loading: false,
            error: null
          });
          store.toastr.success(store.translate.instant('team.notifications.created'), store.translate.instant('notifications.success'));
        },
        error: (error: StoreErrorHandler) => {
          updateState(store, '[Farm Members] Add Member Error', {
            loading: false,
            error: error.message || 'errors.createFailed'
          });
          store.toastr.error(store.translate.instant(error.message || 'errors.createFailed'), store.translate.instant('notifications.error'));
        }
      });
    },

    updateFarmMember(farmId: string, memberId: string, updates: FarmMemberUpdateRequest): void {
      updateState(store, '[Farm Members] Update Member Start', { loading: true, error: null });

      store.farmMembersService.patchFarmMember(farmId, memberId, updates).subscribe({
        next: () => {
          const currentMember = store.members().find(m => m.user_id === memberId);
          if (!currentMember) {
            updateState(store, '[Farm Members] Update Member Error', {
              loading: false,
              error: 'Member not found in store after update'
            });
            return;
          }

          const updatedMember: FarmMember = {
            ...currentMember,
            ...updates,
            updated_at: new Date().toISOString()
          };

          const newMembers = updateMemberList(store.members(), updatedMember);

          updateState(store, '[Farm Members] Update Member Success', {
            members: newMembers,
            loading: false,
            error: null
          });

          if (store.selectedMember()?.user_id === memberId) {
            updateState(store, '[Farm Members] Update Selected Member', { selectedMember: updatedMember });
          }
          store.toastr.success(store.translate.instant('team.notifications.updated'), store.translate.instant('notifications.success'));
        },
        error: (error: StoreErrorHandler) => {
          updateState(store, '[Farm Members] Update Member Error', {
            loading: false,
            error: error.message || 'errors.updateFailed'
          });
          store.toastr.error(store.translate.instant(error.message || 'errors.updateFailed'), store.translate.instant('notifications.error'));
        }
      });
    },

    patchFarmMember(farmId: string, memberId: string, patch: FarmMemberUpdateRequest): void {
      updateState(store, '[Farm Members] Patch Member Start', { loading: true, error: null });

      store.farmMembersService.patchFarmMember(farmId, memberId, patch).subscribe({
        next: () => {
          const currentMember = store.members().find(m => m.user_id === memberId);
          if (!currentMember) {
            updateState(store, '[Farm Members] Patch Member Error', {
              loading: false,
              error: 'Member not found in store after patch'
            });
            return;
          }

          const patchedMember: FarmMember = {
            ...currentMember,
            ...patch,
            updated_at: new Date().toISOString()
          };

          const newMembers = updateMemberList(store.members(), patchedMember);

          updateState(store, '[Farm Members] Patch Member Success', {
            members: newMembers,
            loading: false,
            error: null
          });

          if (store.selectedMember()?.user_id === memberId) {
            updateState(store, '[Farm Members] Update Selected Member', { selectedMember: patchedMember });
          }
          store.toastr.success(store.translate.instant('team.notifications.updated'), store.translate.instant('notifications.success'));
        },
        error: (error: StoreErrorHandler) => {
          updateState(store, '[Farm Members] Patch Member Error', {
            loading: false,
            error: error.message || 'errors.updateFailed'
          });
          store.toastr.error(store.translate.instant(error.message || 'errors.updateFailed'), store.translate.instant('notifications.error'));
        }
      });
    },

    deleteFarmMember(farmId: string, memberId: string): void {
      updateState(store, '[Farm Members] Delete Member Start', { loading: true, error: null });

      store.farmMembersService.deleteFarmMember(farmId, memberId).subscribe({
        next: () => {
          const newMembers = store.members().filter(member => member.user_id !== memberId);
          updateState(store, '[Farm Members] Delete Member Success', {
            members: newMembers,
            totalMembers: Math.max(0, store.totalMembers() - 1),
            loading: false,
            error: null
          });

          if (store.selectedMember()?.user_id === memberId) {
            updateState(store, '[Farm Members] Clear Selected Member', { selectedMember: null });
          }
          store.toastr.success(store.translate.instant('team.notifications.deleted'), store.translate.instant('notifications.success'));
        },
        error: (error: StoreErrorHandler) => {
          updateState(store, '[Farm Members] Delete Member Error', {
            loading: false,
            error: error.message || 'errors.deleteFailed'
          });
          store.toastr.error(store.translate.instant(error.message || 'errors.deleteFailed'), store.translate.instant('notifications.error'));
        }
      });
    },

    selectMember(member: FarmMember | null): void {
      updateState(store, '[Farm Members] Select Member', { selectedMember: member });
    },

    selectMemberByUserId(userId: string): void {
      const member = store.members().find(m => m.user_id === userId) || null;
      this.selectMember(member);
    },

    loadNextPage(): void {
      const farmId = store.currentFarmId();
      if (!store.hasMoreMembers() || !farmId) return;

      const skip = store.currentPage() * store.pageSize();
      const params: FarmMemberQueryParams = {
        skip,
        limit: store.pageSize()
      };

      this.loadFarmMembers(farmId, params, store.filters(), true);
    },

    clearSearch(): void {
      updateState(store, '[Farm Members] Clear Search', { searchQuery: '' });
      const farmId = store.currentFarmId();
      if (farmId) {
        this.loadFarmMembers(farmId);
      }
    },

    refreshMembers(): void {
      const farmId = store.currentFarmId();
      if (farmId) {
        this.loadFarmMembers(farmId);
      }
    },

    clearError(): void {
      updateState(store, '[Farm Members] Clear Error', { error: null });
    },

    reset(): void {
      updateState(store, '[Farm Members] Reset', initialState);
    },

    getMemberByUserId(userId: string): FarmMember | undefined {
      return store.members().find(member => member.user_id === userId);
    },

    hasMember(userId: string): boolean {
      return store.members().some(member => member.user_id === userId);
    },

    loadInvitations(farmId: string): void {
      updateState(store, '[Farm Members] Load Invitations Start', {
        loading: true,
        error: null,
        currentFarmId: farmId
      });

      store.farmMembersService.getInvitations(farmId).subscribe({
        next: (invitations: ExistingFarmMemberInvitation[]) => {
          updateState(store, '[Farm Members] Load Invitations Success', {
            invitations,
            totalInvitations: invitations.length,
            loading: false,
            error: null
          });
        },
        error: (error: StoreErrorHandler) => {
          updateState(store, '[Farm Members] Load Invitations Error', {
            loading: false,
            error: error.message || 'errors.loadFailed'
          });
          store.toastr.error(store.translate.instant(error.message || 'errors.loadFailed'), store.translate.instant('notifications.error'));
        }
      });
    },

    acceptInvitation(farmId: string, invitationCode: string): void {
      updateState(store, '[Farm Members] Accept Invitation Start', { loading: true, error: null });

      store.farmMembersService.acceptInvitation(farmId, invitationCode).subscribe({
        next: (newMember: FarmMember) => {
          const updatedInvitations = store.invitations().filter(inv => inv.code !== invitationCode);
          const newMembers = updateMemberList(store.members(), newMember);

          updateState(store, '[Farm Members] Accept Invitation Success', {
            members: newMembers,
            invitations: updatedInvitations,
            totalMembers: store.totalMembers() + 1,
            totalInvitations: updatedInvitations.length,
            loading: false,
            error: null
          });
          store.toastr.success(store.translate.instant('team.notifications.invitationAccepted'), store.translate.instant('notifications.success'));
        },
        error: (error: StoreErrorHandler) => {
          updateState(store, '[Farm Members] Accept Invitation Error', {
            loading: false,
            error: error.message || 'errors.acceptFailed'
          });
          store.toastr.error(store.translate.instant(error.message || 'errors.acceptFailed'), store.translate.instant('notifications.error'));
        }
      });
    },

    rejectInvitation(farmId: string, invitationCode: string): void {
      updateState(store, '[Farm Members] Reject Invitation Start', { loading: true, error: null });

      store.farmMembersService.rejectInvitation(farmId, invitationCode).subscribe({
        next: () => {
          const updatedInvitations = store.invitations().filter(inv => inv.code !== invitationCode);

          updateState(store, '[Farm Members] Reject Invitation Success', {
            invitations: updatedInvitations,
            totalInvitations: updatedInvitations.length,
            loading: false,
            error: null
          });
          store.toastr.success(store.translate.instant('team.notifications.invitationRejected'), store.translate.instant('notifications.success'));
        },
        error: (error: StoreErrorHandler) => {
          updateState(store, '[Farm Members] Reject Invitation Error', {
            loading: false,
            error: error.message || 'errors.rejectFailed'
          });
          store.toastr.error(store.translate.instant(error.message || 'errors.rejectFailed'), store.translate.instant('notifications.error'));
        }
      });
    },
  }))
);
